import { ChangeDetectionStrategy, Component, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EfValidationCheckComponent } from '../validation-check/validation-check.component';

export type ValidationCheckStatus = 'idle' | 'valid' | 'invalid';

const passwordPatterns = {
  minLength: '.{8,}',
  upperCase: '[A-Z]',
  lowerCase: '[a-z]',
  digitOrSpecial: '[\\d@$!%*#?&^_-]',
};

export const passwordRegexTemplate = new RegExp(
  `(?=.*${passwordPatterns.digitOrSpecial})(?=.*${passwordPatterns.upperCase})(?=.*${passwordPatterns.lowerCase})${passwordPatterns.minLength}`,
);

@Component({
  selector: 'ef-password-checks',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, EfValidationCheckComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './password-checks.component.html',
})
@UntilDestroy()
export class EfPasswordChecksComponent implements OnInit {
  public readonly passwordPartsValidation = signal<Record<keyof typeof passwordPatterns, ValidationCheckStatus>>({
    minLength: 'idle',
    upperCase: 'idle',
    lowerCase: 'idle',
    digitOrSpecial: 'idle',
  });

  public readonly passwordFormControl = input<FormControl<string | null>>();
  public readonly label = input<string>('');

  private setPasswordPartsValidationState(invalidKey: Exclude<ValidationCheckStatus, 'valid'>): void {
    const password = this.passwordFormControl()!;
    const partValidation: Record<string, ValidationCheckStatus> = {};

    Object.keys(passwordPatterns).forEach((key) => {
      const regex = new RegExp(passwordPatterns[key as keyof typeof passwordPatterns]);
      const isMatch = regex.test(password.value || '');
      partValidation[key] = isMatch ? 'valid' : invalidKey;
    });

    this.passwordPartsValidation.set(partValidation);
  }

  public setPasswordPartsInValidState() {
    this.setPasswordPartsValidationState('invalid');
  }

  ngOnInit(): void {
    this.passwordFormControl()
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.setPasswordPartsValidationState('idle');
      });
  }
}
